import { getAllClients } from '@api/FW/main.api';
import { LoginMethod, trackEvent } from '@app/services/analytics.service';
import { deleteLocalStorageData, readLocalStorageData } from '@app/services/localStorage.service';
import { notificationService } from '@app/services/notification.service';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import { setAllClients, setSelectedClient } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@hooks/shared/reduxHooks';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';

const EXCLUDED_REDIRECTS = ['/auth/login', '/logout'];

interface LoginRedirectOptions {
	/** How the user authenticated. Omitted for the demo account, which is not a funnel completion. */
	loginMethod?: LoginMethod;
}

/**
 * Post-login routing shared by every entry point — password, phone OTP and the
 * SMS second step.
 *
 * A user with access to more than one counterparty picks one before we route
 * anywhere; a single-counterparty user is selected implicitly. After that
 * everyone resumes the deep link that triggered the 401, or lands on the root.
 */
export const useLoginRedirect = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const redirectUrl = readLocalStorageData(LocalStorageItems.redirectUrl);

	return async ({ loginMethod }: LoginRedirectOptions = {}) => {
		// Authenticated is decided here, before the client list is fetched — a
		// failure loading clients must not hide a successful login.
		if (loginMethod) {
			trackEvent('login', { login_status: 'completed', login_method: loginMethod });
		}

		const clients = await getAllClients();

		dispatch(setAllClients(clients));

		// Every wallet / card request is keyed by `user.client`, so a user the backend
		// reports no counterparty for has nothing to work with: letting them through
		// leaves the cabinet authenticated but permanently empty, with the tables stuck
		// on their loading skeletons. Fail loudly instead of half-opening the cabinet.
		if (!clients.length) {
			notificationService.error({ message: t('auth.noClientsError') });
			navigate('/logout');
			return;
		}

		if (clients.length > 1) {
			navigate('/auth/selectClient');
			return;
		}

		if (clients.length === 1) dispatch(setSelectedClient(clients[0]));

		if (redirectUrl) deleteLocalStorageData(LocalStorageItems.redirectUrl);

		navigate(redirectUrl && !EXCLUDED_REDIRECTS.includes(redirectUrl) ? redirectUrl : '/');
	};
};
